import { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

// Inline amount editor for a Bill row. Click-to-edit the estimated_due_amount
// with Enter to save, Escape to cancel. Used on the Dashboard "Bills that
// need to be paid this pay period" list and inside the Reconcile walkthrough
// card so users can correct the actual billed amount without leaving the flow.
//
// Backend contract:
//   PATCH /api/bills/{id} { estimated_due_amount: <n> }
// If the bill is in the paid family (paid/cleared/pending) the backend also
// auto-syncs paid_amount to the new amount — safe to call from either surface.
export default function BillAmountCell({ bill, size = "md", onUpdated, testIdPrefix = "bill-amount" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(bill.estimated_due_amount ?? 0));
  const [saving, setSaving] = useState(false);

  // Keep local input in sync when the bill prop changes underneath us
  // (e.g. after a reschedule or bulk update).
  useEffect(() => {
    if (!editing) setValue(String(bill.estimated_due_amount ?? 0));
  }, [bill.estimated_due_amount, editing]);

  const save = async () => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid amount"); return; }
    if (n === Number(bill.estimated_due_amount)) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/bills/${bill.id}`, { estimated_due_amount: n });
      onUpdated && onUpdated(data);
      toast.success(`${bill.name} → ${formatMoney(n)}`);
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Blur = commit. Users expect "click away" to save what they typed —
  // requiring the ✓ button or Enter for every edit was silently dropping
  // amount changes on the Dashboard. Ignore blurs caused by clicking the
  // Save/Cancel buttons themselves so those handlers own the outcome.
  const onBlur = (e) => {
    const nextEl = e.relatedTarget;
    const tid = nextEl?.getAttribute?.("data-testid") || "";
    if (tid.endsWith("-save") || tid.endsWith("-cancel")) return;
    if (saving) return;
    save();
  };

  const cancel = () => {
    setEditing(false);
    setValue(String(bill.estimated_due_amount ?? 0));
  };

  const displayCls = size === "lg"
    ? "font-heading text-2xl font-semibold text-[#2C302E]"
    : "font-heading text-lg font-semibold text-[#2C302E]";
  const inputCls = size === "lg" ? "w-28 text-lg py-1" : "w-24 text-base py-0.5";

  if (editing) {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap" data-testid={`${testIdPrefix}-editing`}>
        <input
          type="number"
          step="0.01"
          min="0"
          className={`${inputCls} px-2 text-right rounded-md border border-[#4A6148] focus:outline-none text-[#2C302E]`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") cancel();
          }}
          autoFocus
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          className="btn-primary !px-1.5 !py-0.5 text-xs"
          onClick={save}
          disabled={saving}
          data-testid={`${testIdPrefix}-save`}
        >
          <Check size={12} />
        </button>
        <button
          type="button"
          className="btn-ghost !px-1.5 !py-0.5 text-xs"
          onClick={cancel}
          data-testid={`${testIdPrefix}-cancel`}
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Actual bill came in different? Click to edit."
      className={`${displayCls} whitespace-nowrap hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none`}
      data-testid={`${testIdPrefix}-display`}
    >
      {formatMoney(bill.estimated_due_amount)}
    </button>
  );
}
